import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {ChatMessage} from './ai-chat-window.types';
import {omitKey} from '@Utils/record.util';

export interface ChatState {
  messages: Record<string, ChatMessage>;
  isStreaming: boolean;
  error: string | null;
  isRefreshing: boolean;
  hasWriteAccess: boolean;
  isDeleted: boolean;
  useSmartAssetSelection: boolean | null;
}

export type AiChatWindowReduxState = Record<string, ChatState | undefined>;

interface SetMessagesAction {
  chatId: string;
  messages: Record<string, ChatMessage>;
}

interface SetIsStreamingAction {
  chatId: string;
  isStreaming: boolean;
}

interface SetErrorAction {
  chatId: string;
  error: string | null;
}

interface UpdateMessageAction {
  chatId: string;
  messageId: string;
  message: ChatMessage;
}

interface SetIsRefreshingAction {
  chatId: string;
  isRefreshing: boolean;
}

interface SetHasWriteAccessAction {
  chatId: string;
  hasWriteAccess: boolean;
}

interface ClearChatStateAction {
  chatId: string;
}

interface SetIsDeletedAction {
  chatId: string;
  isDeleted: boolean;
}

interface SetUseSmartAssetSelectionAction {
  chatId: string;
  useSmartAssetSelection: boolean | null;
}

const aiChatWindowSlice = createSlice({
  name: 'aiChatWindow',
  initialState: {} as AiChatWindowReduxState,
  reducers: {
    setMessages: (state: AiChatWindowReduxState, action: PayloadAction<SetMessagesAction>) => {
      const {chatId, messages} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].messages = messages;
    },
    updateMessage: (state: AiChatWindowReduxState, action: PayloadAction<UpdateMessageAction>) => {
      const {chatId, messageId, message} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].messages[messageId] = message;
    },
    setIsStreaming: (state: AiChatWindowReduxState, action: PayloadAction<SetIsStreamingAction>) => {
      const {chatId, isStreaming} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].isStreaming = isStreaming;
    },
    setError: (state: AiChatWindowReduxState, action: PayloadAction<SetErrorAction>) => {
      const {chatId, error} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].error = error;
    },
    setIsRefreshing: (state: AiChatWindowReduxState, action: PayloadAction<SetIsRefreshingAction>) => {
      const {chatId, isRefreshing} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].isRefreshing = isRefreshing;
    },
    setHasWriteAccess: (state: AiChatWindowReduxState, action: PayloadAction<SetHasWriteAccessAction>) => {
      const {chatId, hasWriteAccess} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].hasWriteAccess = hasWriteAccess;
    },
    setIsDeleted: (state: AiChatWindowReduxState, action: PayloadAction<SetIsDeletedAction>) => {
      const {chatId, isDeleted} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].isDeleted = isDeleted;
    },
    setUseSmartAssetSelection: (state: AiChatWindowReduxState, action: PayloadAction<SetUseSmartAssetSelectionAction>) => {
      const {chatId, useSmartAssetSelection} = action.payload;
      state[chatId] ??= {messages: {}, isStreaming: false, error: null, isRefreshing: false, hasWriteAccess: true, isDeleted: false, useSmartAssetSelection: null};
      state[chatId].useSmartAssetSelection = useSmartAssetSelection;
    },
    clearChatState: (state: AiChatWindowReduxState, action: PayloadAction<ClearChatStateAction>) => {
      const {chatId} = action.payload;
      return omitKey(state, chatId);
    },
  },
});

export const aiChatWindowReducer = aiChatWindowSlice.reducer;
export const aiChatWindowActions = aiChatWindowSlice.actions;
